import {
  Editor,
  ViewElement,
  clickOutsideHandler,
  ContextualBalloon,
  View,
  Emitter,
  EmitterMixin,
} from "ckeditor5";

export type BalloonOptions = {
  shouldShowViewForElement?(element: ViewElement): boolean;
  createView(api: BalloonHelperApi): View;
};

export interface BalloonHelperApi extends Emitter {
  getSelectedElement: () => ViewElement | null;
  hideView: () => void;
}

export default class ContextualBalloonHelper
  extends EmitterMixin()
  implements BalloonHelperApi
{
  private editor: Editor;

  static register(editor: Editor, options: BalloonOptions) {
    return new ContextualBalloonHelper(editor, options);
  }

  private balloon: ContextualBalloon;
  private options: BalloonOptions;
  private view: View | undefined;
  private selectedElement: ViewElement | null = null;

  constructor(editor: Editor, options: BalloonOptions) {
    super();

    this.editor = editor;
    this.balloon = editor.plugins.get(ContextualBalloon);
    this.options = options;

    const updateSelection = () => {
      this.selectedElement =
        editor.editing.view.document.selection.getSelectedElement();

      if (this.selectedElement == null) return;

      if (options.shouldShowViewForElement?.(this.selectedElement)) {
        this.showView();
      }
    };

    editor.ui.on("update", updateSelection);
  }

  getSelectedElement() {
    return this.selectedElement;
  }

  showView(element?: ViewElement) {
    const positionElement = element ?? this.selectedElement;
    if (positionElement == null) return;

    const view = this.ensureView();

    if (this.balloon.hasView(view)) return;

    this.balloon.add({
      view,
      position: this.getPosition(positionElement),
    });

    this.fire("show");
  }

  hideView() {
    if (this.view == null) return;

    this.fire("hide");

    this.balloon.remove(this.view);
    this.editor.editing.view.focus();
  }

  destroy() {
    this.view?.destroy();
  }

  private ensureView() {
    if (this.view != null) return this.view;

    this.view = this.options.createView(this);

    clickOutsideHandler({
      emitter: this.view,
      activator: () => this.balloon.visibleView === this.view,
      contextElements: () => {
        if (this.balloon.view.element == null) return [];
        return [this.balloon.view.element];
      },
      callback: () => this.hideView(),
    });

    return this.view;
  }

  private getPosition(positionElement: ViewElement) {
    const view = this.editor.editing.view;
    return {
      target: () => {
        return view.domConverter.viewRangeToDom(
          view.createRangeOn(positionElement),
        );
      },
    };
  }
}
